import { useEffect, useRef } from 'react'

import { Conversation as ConversationBase } from '@domain-group/suit-theme'

import useArtwork from '@hooks/useArtwork'
import useConversationContainer from '@hooks/useConversationContainer'

import BlankState from '@components/EntityActivity/BlankState'

import { EntityOptions } from '@redux/modules/types'

import Comment from '@components/Comment'
import CreateArtworkReviewButton from '@components/CreateArtworkReviewButton'

import ConversationImageUrl from './assets/conversation.svg'
import CommentWithAttachments from './components/CommentWithAttachments'

interface ConversationContainerProps {
  boxProps?: object,
  commentsBoxProps?: object,
  conversation: {
    campaign_id?: number,
    subject_id: number,
    subject_type: string,
    title?: string,
  },
  requestOptions?: EntityOptions,
  showAddCommentBox?: boolean,
  showCommentAttachments?: boolean,
}

const ConversationContainer = (props: ConversationContainerProps) => {
  const {
    boxProps,
    commentsBoxProps,
    conversation,
    conversation: { subject_id, subject_type },
    // @ts-expect-error TS2339
    performHttpRequests = true,
    requestOptions,
    showAddCommentBox,
    showCommentAttachments,
  } = props

  const conversationContainerPayload = useConversationContainer({
    // @ts-expect-error TS2739
    conversation,
    performHttpRequests: performHttpRequests && !!subject_id && !!subject_type,
  })

  const {
    callbacks: { createCommentWithConversation },
    creating,
    filteredComments,
    filteredCommentsCount,
    hasFilteredComments,
    latestCommentWithAttachment,
    loading,
  } = conversationContainerPayload

  const containerRef = useRef(null)

  useEffect(() => {
    if (containerRef.current){
      // Scroll to bottom of container to show latest comment
      // @ts-expect-error TS2339
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [containerRef.current, filteredCommentsCount])

  const hasComments = !loading && hasFilteredComments

  const CommentComponent = showCommentAttachments ? CommentWithAttachments : Comment

  const artworkPayload = useArtwork({ id: subject_id })
  const { artwork } = artworkPayload

  return (
    <ConversationBase
      addCommentBoxChildren={subject_type === 'Artwork' && (
        <CreateArtworkReviewButton
          artwork={artwork}
          boxProps={{ marginRight: 'medium' }}
        />
      )}
      addCommentBoxProps={{ showPrivacyToggle: false }}
      boxProps={boxProps}
      callbacks={{ createCommentWithConversation }}
      commentsBoxProps={{
        alignItems: !hasComments ? 'center' : undefined,
        height: !hasComments ? 270 : undefined,
        justifyContent: !hasComments ? 'center' : undefined,
        ...commentsBoxProps,
      }}
      commentCount={filteredCommentsCount}
      creating={creating}
      loading={loading}
      requestOptions={requestOptions}
      showAddCommentBox={showAddCommentBox}
    >
      {!hasComments && (
        <BlankState
          imageUrl={ConversationImageUrl}
          title="No Comments"
          subtitle="Keep a detailed history of work, decisions and requests in the conversation."
        />
      )}

      {filteredComments.map((comment, index) => (
        <CommentComponent
          comment={comment}
          // @ts-expect-error TS2339
          hasLatestAttachment={latestCommentWithAttachment?.id === comment.id}
          isLastComment={index === (filteredCommentsCount - 1)}
          key={comment.id}
        />
      ))}
    </ConversationBase>
  )
}

export default ConversationContainer
